@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_driver

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.engineservice.ServiceApi
import org.example.project.engineslot.SLOT_BEREICH_WA
import org.example.project.engineslot.slotDisplayDate
import org.example.project.service.LoginService
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

@Composable
fun DRIVERMeineAuftragsmappe() {
    val t: (String) -> String = { LocalizationManager.get(it) }
    val currentUser = LoginService.getUser()
    val currentUserId = currentUser?.id.orEmpty().trim()
    val currentUserName = listOf(currentUser?.vorname.orEmpty(), currentUser?.nachname.orEmpty())
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .trim()

    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var bookings by remember { mutableStateOf<List<DriverBookingCardModel>>(emptyList()) }
    val documentCounts = remember { mutableStateOf<Map<String, Int>>(emptyMap()) }

    LaunchedEffect(currentUserId) {
        if (currentUserId.isBlank()) {
            loading = false
            bookings = emptyList()
            return@LaunchedEffect
        }
        loading = true
        error = null
        runCatching {
            loadDriverBookingCards(currentUserId, currentUserName, DriverOrderLoadMode.FINAL)
                .distinctBy { driverAuftragsmappeCardKey(it) }
        }.onSuccess { loaded ->
            bookings = loaded
        }.onFailure {
            bookings = emptyList()
            error = it.message ?: t("driver_order_folder_load_failed")
        }
        loading = false
    }

    LaunchedEffect(bookings) {
        documentCounts.value = loadDriverDocumentCounts(bookings)
    }

    DriverMeineAuftragsmappeLayout(
        loading = loading,
        error = error,
        bookings = bookings,
        documentCounts = documentCounts.value
    )
}

@Composable
fun DriverMeineAuftragsmappeLayout(
    loading: Boolean,
    error: String?,
    bookings: List<DriverBookingCardModel>,
    documentCounts: Map<String, Int>,
    initialSelected: DriverBookingCardModel? = null
) {
    Style(DriverOrderStyles)
    val t: (String) -> String = { LocalizationManager.get(it) }
    var search by remember { mutableStateOf("") }
    var selectedBookingKey by remember(initialSelected) { mutableStateOf(initialSelected?.let(::driverOrderPreviewKey).orEmpty()) }
    val selectedBooking = selectedBookingKey
        .takeIf { it.isNotBlank() }
        ?.let { key -> bookings.firstOrNull { driverOrderPreviewKey(it) == key } }
    val filteredBookings = bookings
        .filter { it.matchesDriverAuftragsmappeSearch(search) }
        .sortedWith(
            compareByDescending<DriverBookingCardModel> { it.booking.driverOrderDateKey() }
                .thenBy { it.booking.driverOrderTimeKey() }
                .thenBy { it.currentTargetLabel }
        )

    Div({ classes(DriverOrderStyles.page) }) {
        Div({ classes(DriverOrderStyles.stickyHeader) }) {
            H3({ classes(DriverOrderStyles.title) }) { Text(t("driver_order_folder")) }
            if (selectedBooking == null) {
                Input(InputType.Text) {
                    classes(DriverOrderStyles.searchInput)
                    value(search)
                    onInput { search = it.value }
                    attr("placeholder", t("driver_search"))
                }
            }
        }
        if (selectedBooking != null) {
            DriverAuftragsmappeDocumentsView(
                card = selectedBooking
            )
            return@Div
        }
        when {
            loading -> Div({ classes(DriverOrderStyles.noticeCard) }) { Text(t("driver_loading_order_folder")) }
            error != null -> Div({ classes(DriverOrderStyles.errorCard) }) { Text(error) }
            filteredBookings.isEmpty() -> Div({ classes(DriverOrderStyles.noticeCard) }) { Text(t("driver_no_completed_orders")) }
            else -> Div({ classes(DriverOrderStyles.grid) }) {
                filteredBookings.forEach { card ->
                    val key = driverOrderPreviewKey(card)
                    DriverAuftragsmappePreviewCard(
                        card = card,
                        documentCount = documentCounts[key] ?: 0,
                        onOpen = { selectedBookingKey = key }
                    )
                }
            }
        }
    }
}

@Composable
private fun DriverAuftragsmappePreviewCard(
    card: DriverBookingCardModel,
    documentCount: Int,
    onOpen: () -> Unit
) {
    val t: (String) -> String = { LocalizationManager.get(it) }
    val date = card.booking.datum.ifBlank { card.booking.verladedatum }
    val time = card.booking.uhrzeitVon.ifBlank { card.booking.verladeuhrzeit }.ifBlank { card.booking.uhrzeitAnmeldung }
    val artLabel = if (card.artCode == SLOT_BEREICH_WA) t("driver_loaded") else t("driver_delivered")
    val addressLines = driverAuftragsmappeAddressLines(card)

    Div({
        classes(DriverOrderStyles.previewTile)
        onClick { onOpen() }
    }) {
        Span({ classes(DriverOrderStyles.previewArt) }) { Text(artLabel) }
        Span({ classes(DriverOrderStyles.previewDate) }) {
            Text("${slotDisplayDate(date)} | ${time.ifBlank { "-" }} ${t("driver_time_suffix")}")
        }
        Span({ classes(DriverOrderStyles.previewRef) }) {
            Text("${t("driver_delivery_note_short")}: ${card.booking.lieferscheinnummer.ifBlank { card.primaryRef }.ifBlank { "-" }}")
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(2.px)
            }
        }) {
            addressLines.forEach { line ->
                Span({ classes(DriverOrderStyles.previewAddress) }) { Text(line) }
            }
        }
        Div({ classes(DriverOrderStyles.documentLinkRow) }) {
            Img(src = "images/dokumente.svg") {
                classes(DriverOrderStyles.documentLinkIcon)
            }
            Span({ classes(DriverOrderStyles.documentLinkText) }) {
            Text(t("driver_documents_available").replace("{count}", documentCount.toString()))
            }
        }
    }
}

@Composable
fun DriverAuftragsmappeDocumentsView(
    card: DriverBookingCardModel
) {
    val t: (String) -> String = { LocalizationManager.get(it) }
    val scope = rememberCoroutineScope()
    var loading by remember(card) { mutableStateOf(true) }
    var documents by remember(card) { mutableStateOf<List<All>>(emptyList()) }
    var error by remember(card) { mutableStateOf("") }
    val serviceType = driverAuftragsmappeDocumentType(card)
    val entryId = driverAuftragsmappeEntryId(card)

    LaunchedEffect(card) {
        loading = true
        error = ""
        documents = runCatching { ServiceApi.serviceDocuments(serviceType, entryId) }
            .onFailure { error = it.message ?: t("driver_documents_load_failed") }
            .getOrDefault(emptyList())
        loading = false
    }

    Div({
        classes(DriverOrderStyles.documentCenter)
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            width(100.percent)
        }
    }) {
        when {
            loading -> Div({ classes(DriverOrderStyles.noticeCard) }) { Text(t("driver_loading_documents")) }
            error.isNotBlank() -> Div({ classes(DriverOrderStyles.errorCard) }) { Text(error) }
            documents.isEmpty() -> Div({ classes(DriverOrderStyles.noticeCard) }) { Text(t("driver_no_documents")) }
            else -> DriverDocumentFolder(
                reference = card.booking.lieferscheinnummer.ifBlank { card.primaryRef },
                documents = documents,
                multiWa = card.artCode == SLOT_BEREICH_WA && documents.driverAuftragsmappeHasMultiWaDocuments(),
                onView = { doc -> scope.launch { driverAuftragsmappeOpenExternal(serviceType, entryId, doc) } },
                footer = { DriverDocumentSharing(card, documents.size) }
            )
        }
    }
}

@Composable
internal fun DriverDocumentFolder(
    reference: String,
    documents: List<All>,
    multiWa: Boolean = documents.driverAuftragsmappeHasMultiWaDocuments(),
    onView: (All) -> Unit,
    footer: @Composable () -> Unit = {}
) {
    Style(DriverOrderStyles)
    Div({ attr("data-driver-document-folder", "true"); classes(DriverOrderStyles.documentCenter); style {
        width(100.percent); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px)
    } }) {
        H3({ style { margin(0.px); marginBottom(8.px); color(Color.white); textAlign("center") } }) {
            Text("Auftrag $reference")
        }
        documents.forEach { doc -> DriverAuftragsmappeDocumentRow(doc, multiWa) { onView(doc) } }
        footer()
    }
}

@Composable
private fun DriverAuftragsmappeDocumentRow(
    doc: All,
    multiWa: Boolean,
    onView: () -> Unit
) {
    Div({
        classes(DriverOrderStyles.documentRow)
        attr("data-driver-document", doc.id)
        attr("role", "button")
        attr("tabindex", "0")
        onClick { onView() }
        onKeyDown { if (it.key == "Enter" || it.key == " ") { it.preventDefault(); onView() } }
    }) {
        Img(src = "images/dokumente.svg") {
            classes(DriverOrderStyles.documentRowIcon)
        }
        Span({ classes(DriverOrderStyles.documentRowText) }) { Text(driverAuftragsmappeLabel(doc, multiWa)) }
    }
}

private fun driverAuftragsmappeAddressLines(card: DriverBookingCardModel): List<String> {
    val address = card.adresseLabel.ifBlank {
        card.booking.driverAuftragsmappeBookingAddress()
    }
    return listOf(
        card.firmaLabel.ifBlank { card.currentTargetLabel }.ifBlank { card.booking.firma },
        address
    ).filter { it.isNotBlank() }
}

private fun All.driverAuftragsmappeBookingAddress(): String =
    listOf(strasse, hausnummer, plz, ort)
        .filter { it.isNotBlank() }
        .joinToString(" ")

suspend fun loadDriverDocumentCounts(cards: List<DriverBookingCardModel>): Map<String, Int> =
    cards.associate { card ->
        val docs = runCatching {
            ServiceApi.serviceDocuments(driverAuftragsmappeDocumentType(card), driverAuftragsmappeEntryId(card))
        }.getOrDefault(emptyList())
        driverOrderPreviewKey(card) to docs.size
    }

private fun DriverBookingCardModel.matchesDriverAuftragsmappeSearch(query: String): Boolean {
    val q = query.trim().lowercase()
    if (q.isBlank()) return true
    return listOf(
        primaryRef,
        booking.lieferscheinnummer,
        firmaLabel,
        adresseLabel,
        booking.lkwkennzeichen,
        booking.aufliegerkennzeichen,
        booking.fahrername,
        currentTargetLabel
    ).any { it.lowercase().contains(q) }
}

fun driverAuftragsmappeDocumentType(card: DriverBookingCardModel): String =
    if (card.artCode == SLOT_BEREICH_WA) "yard-wa" else "yard-we"

fun driverAuftragsmappeEntryId(card: DriverBookingCardModel): String =
    card.booking.lieferscheinnummer
        .ifBlank { card.primaryRef.substringBefore(",").trim() }
        .ifBlank { card.booking.id.ifBlank { card.booking.lfdNr.toString() } }

private fun driverAuftragsmappeCardKey(card: DriverBookingCardModel): String =
    "${driverAuftragsmappeDocumentType(card)}:${driverAuftragsmappeEntryId(card)}"

private fun driverAuftragsmappeFileName(doc: All): String =
    doc.file
        .ifBlank { doc.name.takeIf { it.isNotBlank() }?.let { "$it.pdf" }.orEmpty() }

private fun driverAuftragsmappeLabel(doc: All, multiWa: Boolean): String {
    val fileName = driverAuftragsmappeFileName(doc)
    val base = driverAuftragsmappeClearName(fileName)
        .ifBlank { driverAuftragsmappeClearName(doc.bezeichnung.ifBlank { doc.name }) }
        .ifBlank { doc.bezeichnung.ifBlank { fileName.substringBeforeLast(".") } }
        .ifBlank { LocalizationManager.get("driver_document") }
    val standort = if (multiWa) driverAuftragsmappeWaStandortSuffix(fileName) else ""
    return if (standort.isBlank()) base else "$base $standort"
}

private fun List<All>.driverAuftragsmappeHasMultiWaDocuments(): Boolean =
    mapNotNull { driverAuftragsmappeWaStandortIndex(driverAuftragsmappeFileName(it)) }
        .distinct()
        .size > 1

private fun driverAuftragsmappeWaStandortSuffix(fileName: String): String =
    driverAuftragsmappeWaStandortIndex(fileName)?.let { "${LocalizationManager.get("driver_loading_place")} $it" }.orEmpty()

private fun driverAuftragsmappeWaStandortIndex(fileName: String): String? {
    val name = fileName.substringBeforeLast(".")
    val afterDash = name.substringAfter("-", "")
    val raw = afterDash.takeWhile { it.isDigit() }
    return raw.takeIf { it.isNotBlank() }
}

private fun driverAuftragsmappeClearName(value: String): String {
    val lower = value.lowercase()
    return when {
        lower.contains("paletten") -> LocalizationManager.get("driver_pallet_note")
        lower.contains("prozess") || lower.contains("vorgang") -> LocalizationManager.get("driver_process_document")
        lower.contains("qualit") -> LocalizationManager.get("driver_quality_document")
        lower.contains("lieferschein") -> LocalizationManager.get("driver_delivery_note")
        else -> ""
    }
}

private suspend fun driverAuftragsmappeOpenExternal(type: String, entryId: String, doc: All) {
    val fileName = driverAuftragsmappeFileName(doc)
    ServiceApi.openServiceDocument(type, entryId, fileName)
}
